import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { type RouteProp, useRoute } from '@react-navigation/native';
import { theme, useResponsive } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import {
  AccountPaymentGiftCardLoading,
  AccountScreenContainer,
  AccountScreenTitle,
  GiftCardAccountSection,
  PaymentAccountSection,
  SignedOutView,
  useRedeemGiftCardDefaultValues,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { usePaymentMethods } from '../../hooks';
import type { AccountTabStackParamList } from '../../navigation/AppNavigation.props';

// ─────────────────────────────────────────────────────────────────────────────

export const PaymentAndGiftCardsScreen = () => {
  const { t } = useLocalizationContext();
  const { match } = useResponsive();
  const { paymentMethods, isLoadingPaymentMethods } = usePaymentMethods();

  const { params } =
    useRoute<RouteProp<AccountTabStackParamList, 'PaymentAndGiftCards'>>();
  const giftCardRedemptionEncodedData = params?.giftCardId;

  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const redirectToParams = useMemo(
    () => ({ redirectToGiftCardId: params?.giftCardId }),
    [params?.giftCardId],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return (
      <SignedOutView
        redirectTo="payment-and-gift-cards"
        redirectToParams={redirectToParams}
        withoutFooter
      />
    );
  }

  if (isLoadingPaymentMethods) {
    return <AccountPaymentGiftCardLoading />;
  }

  return (
    <AccountScreenContainer screen="payment-and-gift-cards">
      <AccountScreenTitle
        visibility="COMPACT"
        title={t('account.payments.title')}
      />

      <View style={match([styles.paymentSectionContainerXS, undefined])}>
        <PaymentAccountSection paymentMethods={paymentMethods} />
      </View>

      <GiftCardAccountSection
        giftCardCode={defaultRedemptionValues?.giftCardCode}
        giftCardRegistrationCode={
          defaultRedemptionValues?.giftCardRegistrationCode
        }
      />
    </AccountScreenContainer>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  paymentSectionContainerXS: {
    marginBottom: theme.spacing['10'],
  },
});
